/**
 * Что «Настройки» спрашивают у сервера — этап P2.
 *
 * - тихие часы и «Данные и ИИ» — `platform-api`, `/api/platform/portal/*`;
 * - настройки агента, режим «проводит сам» — `ops-api`, `/api/ops/portal/*`;
 * - подписки и выключатель сбора — `finance-api`, `/api/finance/portal/*`;
 * - свежесть данных — готовый `/api/finance/data` экрана «Данные» дашборда.
 *
 * Правила (пределы значений, проба, «не работает до этапа») — на сервере,
 * теми же функциями, что у ботов; отказ приходит полем `detail`.
 */

import { ApiError } from '@shell/api';
import { request } from '../../api';

export interface QuietHours {
  enabled: boolean;
  quiet_from: string | null;
  quiet_to: string | null;
  text: string;
  message?: string;
}

export interface Subscription {
  code: string;
  title: string;
  hint: string;
  enabled: boolean;
  at: string | null;
  weekday: number | null;
  weekday_name: string | null;
  scheduled: boolean;
  working: boolean;
  stage: string | null;
  explicit: boolean;
}

export interface Subscriptions {
  groups: { title: string; items: Subscription[] }[];
}

export type SettingKind = 'bool' | 'int' | 'money' | 'time' | 'text' | 'inn';

export interface AgentSetting {
  key: string;
  title: string;
  hint: string;
  kind: SettingKind;
  value: boolean | number | string;
  default: boolean | number | string;
  unit: string | null;
  min: number | null;
  max: number | null;
}

export interface AutoPost {
  enabled: boolean;
  effective: boolean;
  trial: { reviewed: number; required: number; false_greens: number; passed: boolean; text: string };
}

export interface AgentSettings {
  groups: { code: 'receipts' | 'restaurant'; title: string; items: AgentSetting[] }[];
  auto_post: AutoPost;
}

export interface Collector {
  code: string;
  title: string | null;
  active: boolean;
  version: string | null;
  last_seen_at: string | null;
  last_batch_at: string | null;
  last_error: string | null;
  last_error_at: string | null;
  entities: { entity: string; rows: number; fetched_at: string | null; received_at: string | null; stale: boolean }[];
}

export interface LlmBudget {
  service: string;
  limit_usd: string | null;
  spent_usd: string;
  percent: number | null;
  over_80: boolean;
  exhausted: boolean;
}

export interface SystemState {
  collectors: Collector[];
  stale_after_minutes: number | null;
  llm: LlmBudget[];
}

export interface Collection {
  paused: boolean;
  text: string;
  message?: string;
}

export interface FinanceData {
  freshness: { headline: string; details: string[]; stale: boolean; started: boolean };
  sources: {
    scope: string;
    started: boolean;
    at?: string;
    synced_until?: string | null;
    completeness_pct?: number | null;
    issues?: string[];
  }[];
}

export const settingsApi = {
  quiet: () => request<QuietHours>('/api/platform/portal/quiet-hours'),
  setQuiet: (body: { enabled: boolean; quiet_from?: string; quiet_to?: string }) =>
    request<QuietHours>('/api/platform/portal/quiet-hours', { method: 'PUT', body }),
  subscriptions: () => request<Subscriptions>('/api/finance/portal/subscriptions'),
  setSubscription: (code: string, body: { enabled?: boolean; at?: string }) =>
    request<Subscriptions>(`/api/finance/portal/subscriptions/${encodeURIComponent(code)}`, {
      method: 'PUT',
      body,
    }),
  agent: () => request<AgentSettings>('/api/ops/portal/settings'),
  setAgent: (key: string, value: unknown) =>
    request<{ message: string; setting: AgentSetting }>(
      `/api/ops/portal/settings/${encodeURIComponent(key)}`,
      { method: 'PUT', body: { value } },
    ),
  setAutoPost: (on: boolean) =>
    request<{ message: string; auto_post: AutoPost }>('/api/ops/portal/auto-post', {
      method: 'PUT',
      body: { on },
    }),
  system: () => request<SystemState>('/api/platform/portal/system'),
  collection: () => request<Collection>('/api/finance/portal/collection'),
  setCollection: (paused: boolean) =>
    request<Collection>('/api/finance/portal/collection', { method: 'PUT', body: { paused } }),
  financeData: () => request<FinanceData>('/api/finance/data'),
};

export const SETTINGS_KEYS = {
  quiet: ['settings', 'quiet'] as const,
  subscriptions: ['settings', 'subscriptions'] as const,
  agent: ['settings', 'agent'] as const,
  system: ['settings', 'system'] as const,
  collection: ['settings', 'collection'] as const,
  financeData: ['settings', 'finance-data'] as const,
};

export function problemText(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.detail) return error.detail;
    if (error.status === 403) return 'Это меняет только владелец системы.';
    if (error.status === 400) return 'Сервер не принял значение. Проверьте, что введено.';
  }
  return 'Не сохранилось. Попробуйте ещё раз.';
}
